import { Form } from "@inertiajs/react"
import { ShoppingBag } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { transitionsMissionCollabPath } from "@/routes"
import type { BrandMissionCollabRow } from "@/types/missions"

import { ReturnToFields } from "./collab-transition-return"

/**
 * OKS-33 — Brand-side `Mark purchased` confirm dialog for the
 * Promotional workflow (UX Spec §2). Operator fires this once they
 * spot the creator's order on their external e-commerce store. The
 * confirm body interpolates the creator's display name so the
 * operator sees who they're confirming.
 *
 * Mirrors `mark-delivered-confirm-dialog.tsx` shape verbatim — single
 * Inertia `<Form>` POST with hidden `event=mark_purchased`. No
 * textarea (notes are `flag_issue`-only).
 */
interface MarkPurchasedConfirmDialogProps {
  row: BrandMissionCollabRow
  missionId: number
}

export function MarkPurchasedConfirmDialog({
  row,
  missionId,
}: MarkPurchasedConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ariaTitle = row.creator.handle
    ? `@${row.creator.handle}`
    : row.creator.name

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant="default"
            size="sm"
            aria-label={t("collabs.mark_purchased.aria", { title: ariaTitle })}
            data-testid="brand-mission-collab-mark-purchased-trigger"
          >
            <ShoppingBag aria-hidden="true" className="mr-1 size-4" />
            {t("collabs.mark_purchased.cta")}
          </Button>
        }
      />
      <DialogContent
        data-testid="brand-mission-collab-mark-purchased-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("collabs.mark_purchased.title")}</DialogTitle>
          <DialogDescription>
            {t("collabs.mark_purchased.body", { creator: row.creator.name })}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={transitionsMissionCollabPath(missionId, row.id)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="event" value="mark_purchased" />
              {/* OKS-1038 — return_to/collabs_shown on the crew member page only. */}
              <ReturnToFields />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid="brand-mission-collab-mark-purchased-cancel"
                >
                  {t("collabs.mark_purchased.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={processing}
                  autoFocus
                  data-testid="brand-mission-collab-mark-purchased-confirm"
                >
                  {t("collabs.mark_purchased.confirm")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
